import {FULFILLED, PENDING} from 'redux-promise-middleware';
import {HTO, CATEGORY, sendMobileAnalyticsEvent} from '@optimaxdev/analytics/mobile';

import {CATEGORY as CATEGORY_ACTION} from 'constants/actions';
import {HTO_CODE_FILTER} from 'constants/category';
import {isHtoEnabled} from 'constants/flags/flags';
import {waitForFulfilled} from 'libs/utils/utils';

import {take, call, select, all, takeEvery} from 'redux-saga/effects';
import {getCategory, getCategoryItemsCountIsFulfilled} from 'selectors/category/category';
import {
    isActiveFilterByCode,
    getFiltersTitleList,
    getIsFiltersDataFulfilled,
} from 'selectors/filters/filters';
import {getIsAvailableWidget} from 'selectors/homeTryOn/homeTryOn';

export type NonInteractiveEventsType = {
    isHTOEnabled: boolean;
    title: string;
};

export type HtoEligibleEventType = {
    isHTOEligible: boolean;
    title: string;
    filters: string[];
};

/**
 * Send non-interactive hto analytics events on CTP
 * @param {NonInteractiveEventsType} category state
 */
export const sendNonInteractiveEvents = ({isHTOEnabled, title}: NonInteractiveEventsType) => {
    sendMobileAnalyticsEvent(
        HTO.CATEGORY_STATUS,
        {
            categoryTitle: title,
            state: isHTOEnabled ? 'Enable' : 'Disable',
        },
        false,
    );
};

/**
 * Send HTO eligible event
 * @param {HtoEligibleEventType} category state
 */
export const sendHtoEligibleEvent = ({isHTOEligible, title, filters}: HtoEligibleEventType) => {
    sendMobileAnalyticsEvent(
        CATEGORY.HTO,
        {
            label: filters.length ? `Filtered ${title} - ${filters.join(', ')}` : title,
            action: isHTOEligible ? 'PLP - HTO Eligible' : 'PLP - HTO Not Eligible',
        },
        false,
    );
};

/**
 * Send HTO non-interaction analytics for category
 */
export function* sendCategoryAnalytics() {
    yield call(
        waitForFulfilled,
        getCategoryItemsCountIsFulfilled,
        `${CATEGORY_ACTION.GET_CATEGORY_ITEMS_COUNT}_${FULFILLED}`,
    );
    yield call(
        waitForFulfilled,
        getIsFiltersDataFulfilled,
        `${CATEGORY_ACTION.GET_FILTERS_CATEGORY}_${FULFILLED}`,
    );

    const [{title}, isHTOEnabled, isWidgetShown, filters] = yield all([
        select(getCategory),
        select(isActiveFilterByCode, HTO_CODE_FILTER),
        select(getIsAvailableWidget),
        select(getFiltersTitleList),
    ]);

    yield call(sendNonInteractiveEvents, {
        isHTOEnabled,
        title,
    });

    if (isHtoEnabled()) {
        yield call(sendHtoEligibleEvent, {
            isHTOEligible: isWidgetShown,
            title,
            filters,
        });
    }
}

/**
 * Send HTO interaction event for category
 */
export function* onFilterChanged() {
    const isWidgetShown = yield select(getIsAvailableWidget);

    yield all([
        take(`${CATEGORY_ACTION.GET_CATEGORY_ITEMS_COUNT}_${FULFILLED}`),
        take(`${CATEGORY_ACTION.GET_CATEGORY_ITEMS}_${FULFILLED}`),
    ]);

    const [{title}, isHTOEnabled, isWidgetShownChanged, filters] = yield all([
        select(getCategory),
        select(isActiveFilterByCode, HTO_CODE_FILTER),
        select(getIsAvailableWidget),
        select(getFiltersTitleList),
    ]);

    if (isWidgetShown === isWidgetShownChanged) return;

    yield call(sendNonInteractiveEvents, {
        isHTOEnabled,
        title,
    });

    if (!isWidgetShownChanged && isHtoEnabled()) {
        yield call(sendHtoEligibleEvent, {
            isHTOEligible: isWidgetShownChanged,
            title,
            filters,
        });
    }
}

/**
 * HTO analytics root saga
 */
export default function* htoCategoryAnalyticsSaga() {
    yield takeEvery(`${CATEGORY_ACTION.GET_CATEGORY_ITEMS}_${PENDING}`, sendCategoryAnalytics);
    yield takeEvery(CATEGORY_ACTION.CHANGED_FILTER, onFilterChanged);
}
